import React, { useState, useEffect, useCallback } from 'react';
import {
  UtensilsCrossed, Calendar, Coffee, Sun as SunIcon, Cookie, Moon,
  Loader2, ChevronDown, ChevronUp, History, Star, AlertCircle,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import apiClient from '@/utils/apiClient';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MEALS = [
  { key: 'breakfast', label: 'Breakfast', icon: Coffee, color: '#F59E0B', bg: '#FFFBEB', border: '#FDE68A', gradient: 'linear-gradient(135deg, #FEF3C7, #FFFBEB)' },
  { key: 'lunch', label: 'Lunch', icon: SunIcon, color: '#10B981', bg: '#ECFDF5', border: '#A7F3D0', gradient: 'linear-gradient(135deg, #D1FAE5, #ECFDF5)' },
  { key: 'snacks', label: 'Snacks', icon: Cookie, color: '#8B5CF6', bg: '#F5F3FF', border: '#DDD6FE', gradient: 'linear-gradient(135deg, #EDE9FE, #F5F3FF)' },
  { key: 'dinner', label: 'Dinner', icon: Moon, color: '#3B82F6', bg: '#EFF6FF', border: '#BFDBFE', gradient: 'linear-gradient(135deg, #DBEAFE, #EFF6FF)' }
];

const DAY_SHORT = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun' };

const NutritionView = () => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [pastPlans, setPastPlans] = useState([]);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState('');

  useEffect(() => {
    // Set today's day as selected by default
    const today = new Date();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    setSelectedDay(dayNames[today.getDay()]);

    // Set today's date string as selected
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    setSelectedCalendarDate(dateStr);
  }, []);

  const fetchPlan = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/v1/parent/nutrition/active-plan');
      setPlan(response.data.plan);
    } catch (e) {
      console.error('Failed to fetch nutrition plan:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPastPlans = useCallback(async () => {
    try {
      const response = await apiClient.get('/v1/parent/nutrition/all-plans');
      setPastPlans(response.data.plans || []);
    } catch (e) {
      console.error('Failed to fetch past plans:', e);
    }
  }, []);

  useEffect(() => {
    fetchPlan();
    fetchPastPlans();
  }, [fetchPlan, fetchPastPlans]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3" style={{ color: '#7AB0FE' }} />
          <p className="text-gray-500">Loading meal plan...</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
          <UtensilsCrossed className="w-10 h-10 text-gray-300" />
        </div>
        <h2 className="text-2xl font-bold text-gray-700 mb-2">No Meal Plan Available</h2>
        <p className="text-gray-400">The hostel hasn't published a meal plan yet. Please check back later.</p>
      </div>
    );
  }

  const getDayDateString = (dayName) => {
    if (!plan || !plan.start_date) return '';
    const offset = DAYS.indexOf(dayName);
    if (offset === -1) return '';
    const base = new Date(plan.start_date);
    base.setDate(base.getDate() + offset);
    return base.toISOString().split('T')[0];
  };

  const getActiveOccasionForDay = (dayName) => {
    const dayDateStr = getDayDateString(dayName);
    if (!dayDateStr || !plan.occasion_plans) return null;
    return plan.occasion_plans.find(occ => {
      return occ.start_date <= dayDateStr && dayDateStr <= occ.end_date;
    });
  };

  const today = new Date();
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const todayName = dayNames[today.getDay()];

  const activeOccasion = selectedDay ? getActiveOccasionForDay(selectedDay) : null;

  return (
    <div className="max-w-6xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-3" style={{ color: '#2C5F7F' }}>
          <UtensilsCrossed className="w-8 h-8 text-blue-500 animate-bounce-slow" />
          Weekly Meal Plan
        </h1>
        <div className="flex items-center gap-3 mt-2">
          <span className="text-gray-500 font-semibold">{plan.week_label}</span>
          <span className="text-gray-300">•</span>
          <span className="text-sm text-gray-400 flex items-center gap-1 font-medium">
            <Calendar className="w-3.5 h-3.5" />
            {plan.start_date} — {plan.end_date}
          </span>
        </div>
      </div>

      {/* Day Selector Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {DAYS.map(day => {
          const isToday = day === todayName;
          const isSelected = day === selectedDay;
          const occasionForDay = getActiveOccasionForDay(day);
          return (
            <button
              key={day}
              onClick={() => setSelectedDay(day)}
              className={`relative flex-shrink-0 px-5 py-3 rounded-xl font-semibold transition-all text-sm flex flex-col items-center justify-center ${
                isSelected
                  ? 'text-white shadow-lg scale-105'
                  : occasionForDay
                    ? 'bg-purple-50/70 text-purple-700 border border-purple-200 hover:bg-purple-100/50'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300 hover:bg-blue-50'
              }`}
              style={isSelected ? { background: occasionForDay ? 'linear-gradient(135deg, #8B5CF6, #6D28D9)' : 'linear-gradient(135deg, #4A90D9, #357ABD)' } : {}}
            >
              <span className="block text-xs opacity-75">{DAY_SHORT[day]}</span>
              <span className="block flex items-center gap-1">
                {day}
                {occasionForDay && (
                  <Star className={`w-3.5 h-3.5 ${isSelected ? 'text-yellow-300 fill-yellow-200' : 'text-purple-500 fill-purple-200'} animate-pulse`} />
                )}
              </span>
              {isToday && (
                <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${isSelected ? 'bg-yellow-300' : 'bg-blue-400'} border-2 border-white`}></span>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected Day's Meals */}
      {selectedDay && (
        <div className="mb-8">
          {activeOccasion && (
            <div className="mb-6 p-5 rounded-2xl border border-purple-200 shadow-md relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FAF5FF 0%, #F5F3FF 100%)' }}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-purple-100/40 rounded-full -mr-8 -mt-8 flex items-center justify-center">
                <Star className="w-12 h-12 text-purple-200 fill-purple-300" />
              </div>
              <div className="flex items-center gap-3">
                <span className="p-3 bg-purple-100 rounded-xl text-purple-600">
                  <Star className="w-6 h-6 fill-purple-400" />
                </span>
                <div>
                  <span className="px-2.5 py-1 text-[10px] font-black bg-purple-200 text-purple-800 rounded-full tracking-wider uppercase">Special Occasion Menu</span>
                  <h3 className="text-xl font-bold text-purple-950 mt-1">✨ {activeOccasion.week_label}</h3>
                  <p className="text-sm text-purple-700 mt-0.5 font-medium">A special festive menu is served today instead of the regular base meals!</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {MEALS.map(meal => {
              const items = activeOccasion
                ? (activeOccasion.meals?.[selectedDay]?.[meal.key] || [])
                : (plan.meals?.[selectedDay]?.[meal.key] || []);
              const MealIcon = meal.icon;
              return (
                <div key={meal.key} className="rounded-2xl border overflow-hidden shadow-sm hover:shadow-md transition-all duration-300" style={{ borderColor: activeOccasion ? '#D6BCFA' : meal.border }}>
                  {/* Meal Header */}
                  <div className="px-5 py-4 flex items-center gap-3 transition-colors" style={{ background: activeOccasion ? 'linear-gradient(135deg, #FAF5FF, #F5F3FF)' : meal.gradient }}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: activeOccasion ? '#F3E8FF' : (meal.color + '20') }}>
                      <MealIcon className="w-5 h-5" style={{ color: activeOccasion ? '#8B5CF6' : meal.color }} />
                    </div>
                    <div>
                      <h3 className="font-bold" style={{ color: activeOccasion ? '#7C3AED' : meal.color }}>{meal.label}</h3>
                      <p className="text-xs text-gray-400">{items.length} item{items.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>

                  {/* Food Items */}
                  <div className="p-5 bg-white min-h-[100px]">
                    {items.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {items.map((item, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border shadow-sm transition-all"
                            style={{ 
                              background: activeOccasion ? '#F5F3FF' : meal.bg, 
                              color: activeOccasion ? '#6B21A8' : meal.color, 
                              borderColor: activeOccasion ? '#E9D5FF' : meal.border 
                            }}
                          >
                            <span className="w-1.5 h-1.5 rounded-full mr-2" style={{ backgroundColor: activeOccasion ? '#8B5CF6' : meal.color }}></span>
                            {item}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-20 text-gray-300">
                        <AlertCircle className="w-5 h-5 mr-2" />
                        <span className="text-sm">No items listed</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Full Week Overview Table */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden mb-8">
        <div className="px-6 py-4 border-b bg-gray-50/50">
          <h3 className="font-bold text-gray-700 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-400" />
            Full Week Overview
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50/30">
                <th className="px-4 py-3 text-left font-semibold text-gray-500 text-xs uppercase tracking-wider w-24">Day</th>
                {MEALS.map(meal => {
                  const MealIcon = meal.icon;
                  return (
                    <th key={meal.key} className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wider" style={{ color: meal.color }}>
                      <div className="flex items-center gap-1.5">
                        <MealIcon className="w-3.5 h-3.5" />
                        {meal.label}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {DAYS.map((day, idx) => {
                const isToday = day === todayName;
                const occasionForDay = getActiveOccasionForDay(day);
                return (
                  <tr
                    key={day}
                    className={`border-b last:border-0 cursor-pointer transition-colors ${
                      isToday 
                        ? 'bg-blue-50/60' 
                        : occasionForDay 
                          ? 'bg-purple-50/30 hover:bg-purple-100/20' 
                          : idx % 2 === 0 
                            ? 'bg-gray-50/30' 
                            : 'bg-white'
                    } hover:bg-blue-50/40`}
                    onClick={() => setSelectedDay(day)}
                  >
                    <td className="px-4 py-3.5">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-700">{DAY_SHORT[day]}</span>
                          {isToday && (
                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded text-[10px] font-bold">TODAY</span>
                          )}
                        </div>
                        {occasionForDay && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[9px] font-extrabold w-max border border-purple-200">
                            ✨ FESTIVE
                          </span>
                        )}
                      </div>
                    </td>
                    {MEALS.map(meal => {
                      const items = occasionForDay
                        ? (occasionForDay.meals?.[day]?.[meal.key] || [])
                        : (plan.meals?.[day]?.[meal.key] || []);
                      return (
                        <td key={meal.key} className="px-4 py-3.5">
                          <div className="flex flex-wrap gap-1">
                            {items.length > 0 ? items.map((item, i) => (
                              <span key={i} className="px-2 py-0.5 rounded-full text-xs font-medium border" style={{ background: occasionForDay ? '#F5F3FF' : meal.bg, color: occasionForDay ? '#7C3AED' : meal.color, borderColor: occasionForDay ? '#E9D5FF' : 'transparent' }}>
                                {item}
                              </span>
                            )) : (
                              <span className="text-gray-300 text-xs">—</span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Meal History Explorer */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold flex items-center gap-3" style={{ color: '#2C5F7F' }}>
            <Calendar className="w-6 h-6 text-blue-500" />
            Meal History Explorer
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Navigate the calendar and select any date to view what meals were served to your child.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Calendar Column */}
          <div className="lg:col-span-5 bg-gray-50/50 p-5 rounded-2xl border border-gray-100">
            {/* Calendar header with nav */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4 px-2">
              <div className="flex gap-2">
                <select
                  value={calendarDate.getMonth()}
                  onChange={(e) => setCalendarDate(new Date(calendarDate.getFullYear(), parseInt(e.target.value), 1))}
                  className="bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm font-bold text-gray-700 outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer shadow-sm"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i} value={i}>
                      {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
                <select
                  value={calendarDate.getFullYear()}
                  onChange={(e) => setCalendarDate(new Date(parseInt(e.target.value), calendarDate.getMonth(), 1))}
                  className="bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm font-bold text-gray-700 outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer shadow-sm"
                >
                  {Array.from({ length: 11 }, (_, i) => {
                    const y = new Date().getFullYear() - 8 + i; // Dynamically generates past years up to future
                    return <option key={y} value={y}>{y}</option>;
                  })}
                </select>
              </div>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={() => setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                  className="p-1.5 rounded-lg bg-white border border-gray-200 hover:bg-gray-100 text-gray-600 transition-all shadow-sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const today = new Date();
                    setCalendarDate(today);
                    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                    setSelectedCalendarDate(todayStr);
                  }}
                  className="px-2.5 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-bold hover:bg-gray-100 text-gray-600 transition-all shadow-sm"
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                  className="p-1.5 rounded-lg bg-white border border-gray-200 hover:bg-gray-100 text-gray-600 transition-all shadow-sm"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Days of week header */}
            <div className="grid grid-cols-7 text-center text-xs font-black text-gray-400 uppercase tracking-wider mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} className="py-1">{d}</div>
              ))}
            </div>

            {/* Days grid */}
            <div className="grid grid-cols-7 gap-1">
              {(() => {
                const year = calendarDate.getFullYear();
                const month = calendarDate.getMonth();
                const firstDayOfMonth = new Date(year, month, 1);
                const lastDayOfMonth = new Date(year, month + 1, 0);
                const daysInMonth = lastDayOfMonth.getDate();
                const startDayOfWeek = firstDayOfMonth.getDay();

                const cells = [];
                for (let i = 0; i < startDayOfWeek; i++) {
                  cells.push(<div key={`pad-${i}`} className="aspect-square"></div>);
                }

                for (let day = 1; day <= daysInMonth; day++) {
                  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const isSelected = dateStr === selectedCalendarDate;
                  
                  const todayObj = new Date();
                  const todayStr = `${todayObj.getFullYear()}-${String(todayObj.getMonth() + 1).padStart(2, '0')}-${String(todayObj.getDate()).padStart(2, '0')}`;
                  const isToday = dateStr === todayStr;

                  // Find plans covering dateStr
                  let status = null;
                  if (pastPlans && pastPlans.length > 0) {
                    const occasion = pastPlans.find(p => p.plan_type === 'occasion' && p.start_date <= dateStr && dateStr <= p.end_date);
                    if (occasion) status = 'occasion';
                    else {
                      const standard = pastPlans.find(p => p.plan_type !== 'occasion' && p.start_date <= dateStr && dateStr <= p.end_date);
                      if (standard) status = 'standard';
                    }
                  }

                  cells.push(
                    <button
                      key={`day-${day}`}
                      type="button"
                      onClick={() => setSelectedCalendarDate(dateStr)}
                      className={`aspect-square flex flex-col items-center justify-center rounded-xl text-sm font-semibold transition-all relative group ${
                        isSelected
                          ? 'bg-blue-600 text-white shadow-md'
                          : isToday
                          ? 'border-2 border-blue-400 text-blue-600 bg-white hover:bg-blue-50'
                          : 'bg-white text-gray-600 border border-gray-100 hover:border-blue-200 hover:bg-blue-50/50'
                      }`}
                    >
                      <span>{day}</span>
                      {status && (
                        <span className={`absolute bottom-1 w-1.5 h-1.5 rounded-full ${
                          isSelected 
                            ? 'bg-white' 
                            : status === 'occasion' 
                            ? 'bg-purple-500 animate-pulse' 
                            : 'bg-blue-500'
                        }`} />
                      )}
                    </button>
                  );
                }
                return cells;
              })()}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 mt-4 px-2 text-xs text-gray-400">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                <span>Regular Menu</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                <span>Special Festive Menu</span>
              </div>
            </div>
          </div>

          {/* Meals Details Column */}
          <div className="lg:col-span-7">
            {(() => {
              if (!selectedCalendarDate) return null;

              const [year, month, day] = selectedCalendarDate.split('-').map(Number);
              const dateObj = new Date(year, month - 1, day);
              const formattedDisplayDate = dateObj.toLocaleDateString('default', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              });

              // Find plan covering selectedCalendarDate
              let planForDate = null;
              if (pastPlans && pastPlans.length > 0) {
                planForDate = pastPlans.find(p => p.plan_type === 'occasion' && p.start_date <= selectedCalendarDate && selectedCalendarDate <= p.end_date);
                if (!planForDate) {
                  planForDate = pastPlans.find(p => p.plan_type !== 'occasion' && p.start_date <= selectedCalendarDate && selectedCalendarDate <= p.end_date);
                }
              }

              if (!planForDate) {
                return (
                  <div className="h-full min-h-[300px] border border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center text-center p-6 bg-white">
                    <AlertCircle className="w-10 h-10 text-gray-300 mb-3" />
                    <h4 className="font-bold text-gray-700">{formattedDisplayDate}</h4>
                    <p className="text-gray-400 text-sm mt-1 max-w-xs">
                      No meal plan was found in the records for this date.
                    </p>
                  </div>
                );
              }

              const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
              const weekday = weekdays[dateObj.getDay()];
              const isOccasion = planForDate.plan_type === 'occasion';

              return (
                <div className="space-y-4">
                  {/* Day Header Info */}
                  <div className="p-5 rounded-2xl bg-white border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div>
                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        Selected Date
                      </span>
                      <h3 className="text-lg font-black text-gray-800 mt-0.5">{formattedDisplayDate}</h3>
                    </div>
                    <div className="text-left md:text-right">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                        isOccasion 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {isOccasion ? '✨ Festive Menu' : 'Regular Weekly Menu'}
                      </span>
                      <p className="text-xs text-gray-400 mt-1 font-medium">{planForDate.week_label}</p>
                    </div>
                  </div>

                  {/* Meal cards for this date */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {MEALS.map(meal => {
                      const items = planForDate.meals?.[weekday]?.[meal.key] || [];
                      const MealIcon = meal.icon;

                      return (
                        <div key={meal.key} className="rounded-2xl border bg-white overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in" style={{ borderColor: isOccasion ? '#D6BCFA' : meal.border }}>
                          <div className="px-4 py-3 flex items-center gap-2.5" style={{ background: isOccasion ? 'linear-gradient(135deg, #FAF5FF, #F5F3FF)' : meal.gradient }}>
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: isOccasion ? '#F3E8FF' : (meal.color + '20') }}>
                              <MealIcon className="w-4 h-4" style={{ color: isOccasion ? '#8B5CF6' : meal.color }} />
                            </div>
                            <div>
                              <h4 className="font-bold text-sm" style={{ color: isOccasion ? '#7C3AED' : meal.color }}>{meal.label}</h4>
                              <p className="text-[10px] text-gray-400">{items.length} item{items.length !== 1 ? 's' : ''}</p>
                            </div>
                          </div>

                          <div className="p-4 bg-white min-h-[80px]">
                            {items.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5">
                                {items.map((item, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border shadow-sm transition-all"
                                    style={{ 
                                      background: isOccasion ? '#F5F3FF' : meal.bg, 
                                      color: isOccasion ? '#6B21A8' : meal.color, 
                                      borderColor: isOccasion ? '#E9D5FF' : meal.border 
                                    }}
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full mr-1.5" style={{ backgroundColor: isOccasion ? '#8B5CF6' : meal.color }}></span>
                                    {item}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <div className="flex items-center justify-center h-12 text-gray-300">
                                <AlertCircle className="w-4 h-4 mr-1.5" />
                                <span className="text-xs">No items listed</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionView;
